import { useState } from 'react';
import { EVENT_META, addDays, can, describeEvent, formatClock, formatDuration, localDate, type BabyEvent } from '@bb/shared/core';
import { Icon } from '../components';
import { deleteWithUndo } from '../actions';
import { categoryOfEvent, humanDate } from '../lib';
import { useApp } from '../state';
import { useDay } from '../useDay';

function DayStrip({ strip }: { strip: { category: string; from: number; to: number }[] }) {
  return (
    <div>
      <div className="strip" role="img" aria-label="Полоса суток">
        {strip.map((s, i) => (
          <span key={i} className={`dot-${s.category}`} style={{ left: `${(s.from / 1440) * 100}%`, width: `${Math.max(0.6, ((s.to - s.from) / 1440) * 100)}%` }} />
        ))}
      </div>
      <div className="strip-scale">
        <span>0</span>
        <span>6</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>
    </div>
  );
}

function Item({ e, open, onToggle }: { e: BabyEvent; open: boolean; onToggle: () => void }) {
  const ctx = useApp();
  const tz = ctx.family.tz;
  const cat = categoryOfEvent(e);
  const start = Date.parse(e.startedAt);
  const dur = e.endedAt ? (Date.parse(e.endedAt) - start) / 60000 : null;
  const canEdit = can(ctx.family.role, 'events.write');
  const canDelete = e.authorId === ctx.me.user.id ? canEdit : can(ctx.family.role, 'events.deleteOthers');
  const timeRange = e.endedAt ? `${formatClock(start, tz)}–${formatClock(Date.parse(e.endedAt), tz)}` : null;

  const body = (
    <div className="row" style={{ alignItems: 'stretch', width: '100%' }}>
      <span className="time">{formatClock(start, tz)}</span>
      <span className={`bar dot-${cat}`} />
      <span className="grow">
        <span style={{ display: 'block', fontWeight: 600 }}>{describeEvent(e)}</span>
        <span className="tiny muted">
          {dur != null ? `${formatDuration(dur)}${timeRange ? ` · ${timeRange}` : ''}` : e.endedAt === null && EVENT_META[e.type].timed ? 'идёт сейчас' : ''}
          {e.note ? `${dur != null ? ' · ' : ''}${e.note}` : ''}
        </span>
      </span>
    </div>
  );

  return (
    <li style={{ listStyle: 'none' }}>
      <button type="button" className={`list-item${open ? ' open' : ''}`} aria-expanded={open} onClick={onToggle}>
        {body}
        {open && <span className="tiny muted">{e.authorName ? `Добавил(а): ${e.authorName}` : ''}{e.editedBy ? ' · изменено' : ''}</span>}
      </button>
      {open && (canEdit || canDelete) && (
        <div className="row" style={{ margin: '4px 0 10px' }}>
          {canEdit && (
            <button type="button" className="btn btn-secondary grow" onClick={() => ctx.openEditor({ type: e.type, event: e })}>
              <Icon name="edit" size={18} /> Изменить
            </button>
          )}
          {canDelete && (
            <button type="button" className="btn btn-danger grow" onClick={() => void deleteWithUndo(ctx, e)}>
              <Icon name="trash" size={18} /> Удалить
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export function Diary({ initialDate }: { initialDate?: string }) {
  const ctx = useApp();
  const today = localDate(Date.now(), ctx.family.tz);
  const [date, setDate] = useState(initialDate && initialDate <= today ? initialDate : today);
  const [openId, setOpenId] = useState<string | null>(null);
  const day = useDay(date);
  const s = day?.summary;
  const events = day ? [...day.events].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt)) : [];

  return (
    <div className="stack">
      <header className="header">
        <button type="button" className="icon-btn" aria-label="Предыдущий день" onClick={() => setDate(addDays(date, -1))}>
          <Icon name="left" size={18} />
        </button>
        <h1 className="h2 grow" style={{ textAlign: 'center', fontSize: 16 }} data-testid="diary-date">
          {humanDate(date, today)}
        </h1>
        <button type="button" className="icon-btn" aria-label="Следующий день" disabled={date >= today} style={date >= today ? { opacity: 0.35 } : undefined} onClick={() => setDate(addDays(date, 1))}>
          <Icon name="right" size={18} />
        </button>
      </header>

      {!s ? (
        <div className="skeleton" style={{ minHeight: 200 }} />
      ) : (
        <>
          <div className="tiles3">
            <div className="stat cat-sleep">
              <span className="label">Сон</span>
              <span className="value">{formatDuration(s.sleepMinutes)}</span>
            </div>
            <div className="stat cat-feed">
              <span className="label">Кормления</span>
              <span className="value">{s.feedCount}</span>
            </div>
            <div className="stat cat-diaper">
              <span className="label">Подгузники</span>
              <span className="value">{s.diaperCount}</span>
            </div>
          </div>
          <DayStrip strip={s.strip} />

          {events.length === 0 ? (
            <div className="card center" style={{ minHeight: 160 }}>
              <p className="muted">{date === today ? 'Сегодня пока нет записей' : 'В этот день записей нет'}</p>
              {can(ctx.family.role, 'events.write') && (
                <button type="button" className="btn btn-soft" onClick={ctx.openAdd}>
                  Добавить запись
                </button>
              )}
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0 }} aria-label="Записи за день">
              {events.map((e) => (
                <Item key={e.id} e={e} open={openId === e.id} onToggle={() => setOpenId(openId === e.id ? null : e.id)} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
